import { useSetRecoilState } from 'recoil';
import axios, { endPoints } from 'api/request';
import {
    teams,
    draftTeams,
    reposToMigrate,
    isUserAccessConfigFullFilled,
    isAssignReposConfigFullFilled,
    isMigrateRepoConfigFullFilled,
    isMicroServiceConfigFullFilled,
    teamValidationCache,
    sonarQubeConfig,
    jfrogConfig,
    validOnboardingForm,
    isSonarQubeConfigFilled,
    isJfrogConfigFilled,
    sspSubmitOrder,
    pipelineClassificationData,
    newOnboardingPipelineConfig,
    newOnboardingPipelineEligible,
    argoCDConfigData,
} from 'pages/provisioning/day2/github-onboarding-new/store';
import {
    ONBOARDING_CATEGORY,
    COMMON,
    MIGRATE_EXISTING_OPTION,
    JFROG_DEPENDENCY_OPTION,
    PIPELINE_TYPE_GLOBAL_TEMPLATE,
} from 'pages/provisioning/day2/github-onboarding-new/utils/constant';
import {
    filterRemovedMembers,
    getGithubOrgUnit,
    buildJfrogResolvePayload,
    generateSonarQubeValidationPayload,
    checkIfAllReposAssigned,
    extractNewGithubRepositoryNames,
    extractGithubNames,
} from 'pages/provisioning/day2/github-onboarding-new/utils';
import { buildResolvedRepoConfig } from 'pages/provisioning/day2/github-onboarding-new/utils/hooks/useJfrogRepoConfiguration';
import {
    isUserAccessConfigFilled,
    isAssignReposConfigFilled,
    isMigrateRepoConfigFilled,
    isJfrogConfigFilled as isJfrogConfigFilledFn,
    isSonarQubeIntegrationConfigFilled as isSonarQubeConfigFilledFn,
    isMicroserviceConfigFilled,
    isNewOnboardingPipelineConfigFilled,
} from 'pages/provisioning/day2/github-onboarding-new/utils/formValidation';
import logger from 'utils/logger';
import {
    buildValidationCacheData,
    parseTeamsFromValidations,
    parseReposToMigrate,
    parseArgoCDReposToMigrate,
    buildSonarQubeConfig,
    buildMigrateJfrogRepoConfigs,
    buildNewRepoJfrogConfigs,
    buildNewOnboardingPipelineConfig,
} from './utils';

const useInitializeAtomsFromOrderDetails = () => {
    const setOrder = useSetRecoilState(sspSubmitOrder);
    const setTeams = useSetRecoilState(teams);
    const setDraftTeams = useSetRecoilState(draftTeams);
    const setReposToMigrate = useSetRecoilState(reposToMigrate);
    const setTeamValidationCache = useSetRecoilState(teamValidationCache);
    const setIsUserAccessConfigFilled = useSetRecoilState(isUserAccessConfigFullFilled);
    const setIsAssignReposConfigFilled = useSetRecoilState(isAssignReposConfigFullFilled);
    const setIsMigrateRepoConfigFilled = useSetRecoilState(isMigrateRepoConfigFullFilled);
    const setPipelineClassificationData = useSetRecoilState(pipelineClassificationData);
    const setNewOnboardingPipelineConfig = useSetRecoilState(newOnboardingPipelineConfig);
    const setNewOnboardingPipelineEligible = useSetRecoilState(newOnboardingPipelineEligible);
    const setArgoCDConfigData = useSetRecoilState(argoCDConfigData);
    const setIsMicroServiceConfigFilled = useSetRecoilState(isMicroServiceConfigFullFilled);
    const setSonarQubeConfig = useSetRecoilState(sonarQubeConfig);
    const setValidForm = useSetRecoilState(validOnboardingForm);
    const setIsSonarQubeConfigFilled = useSetRecoilState(isSonarQubeConfigFilled);
    const setJfrogConfig = useSetRecoilState(jfrogConfig);
    const setIsJfrogConfigFilled = useSetRecoilState(isJfrogConfigFilled);

    const validateTeamApi = (teamName, organizationUnit) => axios.get(endPoints.day2Operation.GithubOnboarding.validateTeam({ teamName, organizationUnit }));

    const fetchProjectOrgApi = (adoProjectName) => axios.get(endPoints.day2Operation.GithubOnboarding.projectOrganization(adoProjectName));

    const resolveJfrogApi = (payload) => axios.post(endPoints.day2Operation.GithubOnboarding.resolveJfrogGlobalRepoName, payload);
    const detectTechStackApi = (payload) => axios.post(endPoints.day2Operation.GithubOnboarding.detectTechStack, payload);
    const validateSonarQubeApi = (payload) => axios.post(endPoints.day2Operation.GithubOnboarding.validateSonarQube, payload);

    const normalizeServiceAccounts = (serviceAccounts) => {
        if (!Array.isArray(serviceAccounts)) return [];
        return serviceAccounts
            .filter((account) => typeof account === 'string')
            .map((account) => account.trim())
            .filter(Boolean);
    };

    const validateTeam = async (teamName, organizationUnit, removedMembers = []) => {
        try {
            const response = await validateTeamApi(teamName, organizationUnit);
            const responseData = response?.data;
            const teamExists = responseData?.teamExists || false;
            const apiMembers = responseData?.members || [];
            const filteredMembers = filterRemovedMembers(apiMembers, removedMembers);
            return {
                teamExists,
                hasExistingMembers: teamExists && filteredMembers?.length > 0,
                existingOrgMembers: filteredMembers,
                apiMembers,
            };
        } catch (error) {
            logger.error(`Error validating team ${teamName}:`, error);
            return { teamExists: false, hasExistingMembers: false, existingOrgMembers: [], apiMembers: [] };
        }
    };

    const detectJfrogMigrateConfig = async (catalogConfig, parsedReposToMigrateList, orderObj) => {
        const baseConfigs = buildMigrateJfrogRepoConfigs(catalogConfig);
        if (baseConfigs.length === 0) return { repoConfigurations: [] };

        let adoOrganization = orderObj?.adoOrganization || null;
        if (!adoOrganization && catalogConfig?.adoProjectName) {
            try {
                const orgResponse = await fetchProjectOrgApi(catalogConfig.adoProjectName);
                adoOrganization = orgResponse?.azure?.org || null;
            } catch (error) {
                logger.error(`Failed to fetch ADO organization for project "${catalogConfig.adoProjectName}":`, error);
            }
        }
        const orderWithOrg = { ...orderObj, adoOrganization };

        const repoNames = baseConfigs.map((cfg) => {
            const migrateRepo = parsedReposToMigrateList?.find(
                (repo) => repo?.githubName === cfg.repositoryName || repo?.githubEnterpriseName === cfg.repositoryName
            );
            return migrateRepo?.adoName || cfg.repositoryName;
        });

        const payload = {
            adoOrganization: orderWithOrg?.adoOrganization,
            adoProjectName: orderWithOrg?.adoProjectName,
            repoNames,
        };

        try {
            const response = await detectTechStackApi(payload);
            const apiResults = response?.data?.repos || response?.data?.data?.repos || [];
            const lookup = new Map();
            (apiResults || []).forEach((r) => {
                if (r?.repoName) lookup.set(r.repoName, r);
            });

            const repoConfigurations = baseConfigs.map((cfg, i) => {
                const apiRepoName = repoNames[i];
                const apiItem = lookup.get(apiRepoName);
                return {
                    repositoryName: cfg.repositoryName,
                    jfrogGlobalRepo: null,
                    jfrogRepoName: cfg.jfrogRepoName,
                    jfrogRepoDetails: cfg.jfrogRepoName ? { label: cfg.jfrogRepoName, key: cfg.jfrogRepoName, name: cfg.jfrogRepoName } : null,
                    isValidated: !!cfg.jfrogRepoName,
                    packageManager: apiItem?.packageManager || null,
                    resolvedMessage: apiItem?.message || null,
                    resolveError: null,
                    isNoneBuildTool: !apiItem?.packageManager,
                    resolveAttempted: true,
                };
            });

            return { repoConfigurations };
        } catch (error) {
            logger.error('Failed to detect tech stack for JFrog migrate config:', error);
            return {
                repoConfigurations: baseConfigs.map((cfg) => ({
                    ...cfg,
                    jfrogRepoDetails: cfg.jfrogRepoName ? { label: cfg.jfrogRepoName, key: cfg.jfrogRepoName, name: cfg.jfrogRepoName } : null,
                    resolveAttempted: true,
                })),
            };
        }
    };

    const resolveJfrogMigrateConfig = async (catalogConfig, parsedReposToMigrateList, orderObj) => {
        const baseConfigs = buildMigrateJfrogRepoConfigs(catalogConfig);
        if (baseConfigs.length === 0) return { repoConfigurations: [] };

        let adoOrganization = orderObj?.adoOrganization || null;
        if (!adoOrganization && catalogConfig?.adoProjectName) {
            try {
                const orgResponse = await fetchProjectOrgApi(catalogConfig.adoProjectName);
                adoOrganization = orgResponse?.azure?.org || null;
            } catch (error) {
                logger.error(`Failed to fetch ADO organization for project "${catalogConfig.adoProjectName}":`, error);
            }
        }

        const orderWithOrg = { ...orderObj, adoOrganization };

        const payload = buildJfrogResolvePayload({
            isMigrationFlow: true,
            resolveRepos: baseConfigs.map((c) => c.repositoryName),
            order: orderWithOrg,
            reposToMigrateList: parsedReposToMigrateList,
        });

        try {
            const response = await resolveJfrogApi(payload);
            const apiResults = response?.data?.repos || [];
            const repoConfigurations = baseConfigs.map((cfg) => {
                const resolved = buildResolvedRepoConfig({
                    repoName: cfg.repositoryName,
                    isMigrationFlow: true,
                    apiResults,
                    reposToMigrateList: parsedReposToMigrateList,
                    order: orderWithOrg,
                });
                if (resolved.jfrogGlobalRepo) return resolved;
                return { ...resolved, jfrogRepoName: cfg.jfrogRepoName, isValidated: true };
            });
            return { repoConfigurations };
        } catch (error) {
            logger.error('Failed to resolve JFrog migrate config:', error);
            return { repoConfigurations: baseConfigs };
        }
    };

    const resolveJfrogNewConfig = async (catalogConfig, orderObj) => {
        const allConfigs = buildNewRepoJfrogConfigs(catalogConfig);
        const noBuildToolConfigs = allConfigs.filter((c) => c.isNoneBuildTool);
        const resolvableConfigs = allConfigs.filter((c) => !c.isNoneBuildTool);

        if (resolvableConfigs.length === 0) return { repoConfigurations: allConfigs };

        const payload = buildJfrogResolvePayload({
            isMigrationFlow: false,
            resolveRepos: resolvableConfigs.map((c) => c.repositoryName),
            order: orderObj,
            reposToMigrateList: [],
        });

        try {
            const response = await resolveJfrogApi(payload);
            const apiResults = response?.data?.repos || [];
            const resolvedConfigs = resolvableConfigs.map((cfg) => {
                const resolved = buildResolvedRepoConfig({
                    repoName: cfg.repositoryName,
                    isMigrationFlow: false,
                    apiResults,
                    reposToMigrateList: [],
                    order: orderObj,
                });
                if (resolved.jfrogGlobalRepo) return resolved;
                return { ...resolved, jfrogRepoName: cfg.jfrogRepoName, isValidated: true };
            });
            return { repoConfigurations: [...resolvedConfigs, ...noBuildToolConfigs] };
        } catch (error) {
            logger.error('Failed to resolve JFrog new repo config:', error);
            return { repoConfigurations: allConfigs };
        }
    };

    // --- Main initializer ---
    const initializeAtomsFromOrderDetails = async (catalogConfig, orderObj = {}) => {
        if (!catalogConfig) return;

        setTeamValidationCache({});

        const reposToMigrateData = catalogConfig?.repositoryToMigrate || [];
        const isADOMigration = catalogConfig?.platformType === MIGRATE_EXISTING_OPTION.ADO;
        const parsedPipelineData = reposToMigrateData
            ?.filter((repo) => repo?.pipelineIntegration)
            ?.map((repo) => {
                const pi = repo?.pipelineIntegration;
                const isGlobalTemplate = pi?.pipelineType === PIPELINE_TYPE_GLOBAL_TEMPLATE;
                const hasVariableGroupNames = Array.isArray(pi?.variableGroupNames) && pi.variableGroupNames.length > 0;
                let migrateVariables = null;
                if (!isGlobalTemplate) {
                    migrateVariables = hasVariableGroupNames ? COMMON.YES : COMMON.NO;
                }
                return {
                    repoName: repo?.sourceRepositoryName,
                    ...pi,
                    selectedTechStack: pi?.techStack || null,
                    migrateVariables,
                };
            });
        let isMigratingADOPipelines = null;
        if (isADOMigration && reposToMigrateData?.length > 0) {
            isMigratingADOPipelines = parsedPipelineData?.length > 0 ? COMMON.YES : COMMON.NO;
        }

        const parsedArgoCDConfigData = parseArgoCDReposToMigrate(catalogConfig);
        let isMigratingArgoCD = null;
        if (isADOMigration && reposToMigrateData?.length > 0) {
            isMigratingArgoCD = parsedArgoCDConfigData?.length > 0 ? COMMON.YES : COMMON.NO;
        }

        let fetchedAdoOrganization = orderObj?.adoOrganization || null;
        if (!fetchedAdoOrganization && isADOMigration && catalogConfig?.adoProjectName) {
            try {
                const orgResponse = await fetchProjectOrgApi(catalogConfig.adoProjectName);
                fetchedAdoOrganization = orgResponse?.azure?.org || null;
            } catch (error) {
                logger.error(`Failed to fetch ADO organization for pipeline initialization:`, error);
            }
        }

        const isNEWOnboarding = catalogConfig?.onboardingType === ONBOARDING_CATEGORY.NEW;
        const githubRepoList = catalogConfig?.githubRepository || [];
        let isOnboardingPipelines = null;
        if (isNEWOnboarding && githubRepoList.length > 0) {
            isOnboardingPipelines = githubRepoList.some((r) => r?.pipelineIntegration) ? COMMON.YES : COMMON.NO;
        }

        let savedNewOnboardingPipelineConfigs = null;
        if (isNEWOnboarding && githubRepoList.length > 0) {
            savedNewOnboardingPipelineConfigs = buildNewOnboardingPipelineConfig(catalogConfig);
        }

        const derivedPipelineEligible =
            isNEWOnboarding &&
            isOnboardingPipelines === COMMON.YES &&
            Array.isArray(savedNewOnboardingPipelineConfigs) &&
            savedNewOnboardingPipelineConfigs.some((c) => c.pipelineSetupType)
                ? true
                : null;

        setOrder((prev) => ({
            ...prev,
            applicationName: catalogConfig.applicationName || orderObj.applicationName || '',
            appShortName: catalogConfig.applicationShortName || orderObj.appShortName || '',
            isMigratingADOPipelines,
            isMigratingArgoCD,
            ...(isOnboardingPipelines !== null && { isOnboardingPipelines }),
            ...(fetchedAdoOrganization && { adoOrganization: fetchedAdoOrganization }),
            ...(isNEWOnboarding &&
                Array.isArray(orderObj.microservicesConfigs) &&
                orderObj.microservicesConfigs.length > 0 && {
                    microservicesConfigs: orderObj.microservicesConfigs,
                }),
        }));

        // Set pipeline classification data (ADO migration) and new onboarding pipeline
        setPipelineClassificationData(parsedPipelineData);
        setArgoCDConfigData(parsedArgoCDConfigData);
        if (savedNewOnboardingPipelineConfigs !== null) {
            setNewOnboardingPipelineConfig(savedNewOnboardingPipelineConfigs);
        }
        if (derivedPipelineEligible !== null) {
            setNewOnboardingPipelineEligible(derivedPipelineEligible);
        }

        const organizationUnit = getGithubOrgUnit(catalogConfig?.environment || orderObj?.environment);

        // Teams
        const teamValidations = await Promise.all(
            (catalogConfig?.teams || []).map(async (team) => {
                const removedMembers = Array.isArray(team?.removedMembers) ? team?.removedMembers : [];
                const validation = await validateTeam(team?.teamName, organizationUnit, removedMembers);
                return { team, validation };
            })
        );

        const parsedTeams = parseTeamsFromValidations(teamValidations, catalogConfig?.businessApplication);
        const parsedReposToMigrateList = parseReposToMigrate(catalogConfig);

        // set atoms for teams, repos to migrate and validation cache
        setTeamValidationCache(buildValidationCacheData(teamValidations));
        setTeams(parsedTeams);
        setDraftTeams(JSON.parse(JSON.stringify(parsedTeams)));
        setReposToMigrate(parsedReposToMigrateList);

        if (isNEWOnboarding) {
            const orderWithPipeline = { ...orderObj, isOnboardingPipelines };
            setIsMicroServiceConfigFilled(
                isMicroserviceConfigFilled({ order: orderObj }) &&
                    isNewOnboardingPipelineConfigFilled({
                        pipelineConfigs: savedNewOnboardingPipelineConfigs || [],
                        order: orderWithPipeline,
                        isPipelineEligible: derivedPipelineEligible,
                    })
            );
        }

        // set config filled states
        setIsUserAccessConfigFilled(isUserAccessConfigFilled({ teams: parsedTeams }));
        const isADOEdit = orderObj?.isMigratingADO === MIGRATE_EXISTING_OPTION.ADO;
        const availableReposEdit = isNEWOnboarding
            ? (extractNewGithubRepositoryNames(orderObj) || []).filter(Boolean)
            : (extractGithubNames(isADOEdit, parsedReposToMigrateList || []) || []).filter(Boolean);
        setIsAssignReposConfigFilled(isAssignReposConfigFilled({ teams: parsedTeams }) && checkIfAllReposAssigned(parsedTeams, availableReposEdit));
        setIsMigrateRepoConfigFilled(
            isMigrateRepoConfigFilled({
                reposToMigrateList: parsedReposToMigrateList,
                order: { isMigratingADOPipelines, isMigratingArgoCD },
                pipelineClassificationData: parsedPipelineData,
                argoCDConfigData: parsedArgoCDConfigData,
            })
        );

        // SonarQube configuration
        const parsedSonarQubeConfig = buildSonarQubeConfig(catalogConfig);
        if (parsedSonarQubeConfig !== null) {
            let sonarConfigToSet = parsedSonarQubeConfig;

            const shouldLoadSonarOptions = parsedSonarQubeConfig?.requiresIntegration === COMMON.YES;
            if (shouldLoadSonarOptions) {
                const sonarRepositoryNames =
                    catalogConfig?.onboardingType === ONBOARDING_CATEGORY.MIGRATE
                        ? (catalogConfig?.repositoryToMigrate || []).map((repo) => repo?.sourceRepositoryName).filter(Boolean)
                        : (catalogConfig?.githubRepository || []).map((repo) => repo?.githubRepositoryName).filter(Boolean);

                if (sonarRepositoryNames.length > 0) {
                    const sonarValidationPayload = generateSonarQubeValidationPayload({
                        onboardingType: catalogConfig?.onboardingType || orderObj?.onboardingType,
                        adoProjectName: catalogConfig?.adoProjectName || orderObj?.adoProjectName,
                        repositoryNames: sonarRepositoryNames,
                        organizationUnit,
                    });

                    try {
                        const sonarValidationResponse = await validateSonarQubeApi(sonarValidationPayload);
                        if (sonarValidationResponse?.isSuccess && sonarValidationResponse?.data) {
                            const validationDataFromApi = sonarValidationResponse.data;
                            const normalizedOptions = normalizeServiceAccounts(validationDataFromApi?.serviceAccounts);
                            const selectedGroupAccount = parsedSonarQubeConfig?.sonarGroupServiceAccount;

                            const mergedServiceAccountOptions = selectedGroupAccount
                                ? Array.from(new Set([...normalizedOptions, selectedGroupAccount]))
                                : normalizedOptions;

                            sonarConfigToSet = {
                                ...parsedSonarQubeConfig,
                                validationData: validationDataFromApi,
                                serviceAccountsFromApi: mergedServiceAccountOptions,
                                isSonarGroupServiceAccount: mergedServiceAccountOptions.length > 0 || parsedSonarQubeConfig?.isSonarGroupServiceAccount,
                                shouldValidate: false,
                            };
                        }
                    } catch (error) {
                        logger.error('Failed to load SonarQube options in edit mode:', error);
                    }
                }
            }

            setSonarQubeConfig(sonarConfigToSet);
            setValidForm((prev) => ({
                ...prev,
                isServiceAccountValid:
                    sonarConfigToSet?.sonarGroupServiceAccountValid === true ||
                    (sonarConfigToSet?.hasServiceAccount === COMMON.YES && !!sonarConfigToSet?.serviceAccount),
            }));
            setIsSonarQubeConfigFilled(isSonarQubeConfigFilledFn({ config: sonarConfigToSet, order: orderObj, reposToMigrateList: parsedReposToMigrateList }));
        }

        // JFrog configuration
        const hasJfrogIntegration = catalogConfig?.jfrogArtifactoryIntegration;
        if (hasJfrogIntegration === undefined || hasJfrogIntegration === null) return;

        if (hasJfrogIntegration === true) {
            let repoConfigurations = [];

            if (catalogConfig?.onboardingType === ONBOARDING_CATEGORY.MIGRATE) {
                const orderObjForJfrog = fetchedAdoOrganization ? { ...orderObj, adoOrganization: fetchedAdoOrganization } : orderObj;
                const dependencyOptionRaw = catalogConfig?.jfrogDependencyManagement;
                if (dependencyOptionRaw === JFROG_DEPENDENCY_OPTION.PUBLIC_AND_LOCAL) {
                    ({ repoConfigurations } = await detectJfrogMigrateConfig(catalogConfig, parsedReposToMigrateList, orderObjForJfrog));
                } else {
                    ({ repoConfigurations } = await resolveJfrogMigrateConfig(catalogConfig, parsedReposToMigrateList, orderObjForJfrog));
                }
            } else if (catalogConfig?.onboardingType === ONBOARDING_CATEGORY.NEW) {
                const newDependencyOptionRaw = catalogConfig?.jfrogDependencyManagement;
                if (newDependencyOptionRaw === JFROG_DEPENDENCY_OPTION.PUBLIC_AND_LOCAL) {
                    const baseConfigs = buildNewRepoJfrogConfigs(catalogConfig);
                    repoConfigurations = baseConfigs.map((cfg) => ({
                        ...cfg,
                        jfrogRepoDetails: cfg.jfrogRepoName ? { label: cfg.jfrogRepoName, key: cfg.jfrogRepoName, name: cfg.jfrogRepoName } : null,
                        resolveAttempted: true,
                    }));
                } else {
                    ({ repoConfigurations } = await resolveJfrogNewConfig(catalogConfig, orderObj));
                }
            }

            const dependencyRepoOptionRaw = catalogConfig?.jfrogDependencyManagement;
            const knownOptions = [JFROG_DEPENDENCY_OPTION.PUBLIC_ONLY, JFROG_DEPENDENCY_OPTION.PUBLIC_AND_LOCAL];
            const isKnown = knownOptions.includes(dependencyRepoOptionRaw);
            const dependencyRepoOption = isKnown ? dependencyRepoOptionRaw : JFROG_DEPENDENCY_OPTION.PUBLIC_ONLY;

            setJfrogConfig({
                requiresIntegration: COMMON.YES,
                dependencyRepoOption,
                hasArtifactoryName: repoConfigurations.length > 0 ? COMMON.YES : null,
                repoConfigurations,
                projectOrganization: null,
                jfrogRepoTypes: Array.isArray(catalogConfig?.jfrogRepoTypes) ? catalogConfig.jfrogRepoTypes : [],
            });
            setIsJfrogConfigFilled(
                isJfrogConfigFilledFn({
                    config: {
                        requiresIntegration: COMMON.YES,
                        dependencyRepoOption,
                        hasArtifactoryName: repoConfigurations.length > 0 ? COMMON.YES : null,
                        repoConfigurations,
                        projectOrganization: null,
                        jfrogRepoTypes: Array.isArray(catalogConfig?.jfrogRepoTypes) ? catalogConfig.jfrogRepoTypes : [],
                    },
                    order: orderObj,
                    reposToMigrateList: parsedReposToMigrateList,
                })
            );
        } else {
            setJfrogConfig({
                requiresIntegration: COMMON.NO,
                dependencyRepoOption: null,
                hasArtifactoryName: null,
                repoConfigurations: [],
                projectOrganization: null,
                jfrogRepoTypes: [],
            });
            setIsJfrogConfigFilled(true);
        }
    };

    return { initializeAtomsFromOrderDetails };
};

export default useInitializeAtomsFromOrderDetails;
